import { getInstance } from './instance';
import { OperatorIdentity, operatorRequestConfig } from './operatorHeaders';

/**
 * The display-safe part of the DS `customer.ghl` object. Only the book page
 * subdomain and the provisioning status are rendered; the GHL location, user
 * and contact ids are never shown.
 */
export interface CustomerGhl {
  subdomain?: string;
  // DS GhlProvisioningStatus: pending | provisioning | active | failed
  status?: string;
}

export interface Customer {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  status: string;
  stripeId?: string;
  chargifyId?: string;
  chargebeeCustomerId?: string;
  hubspotId?: string;
  accountManager?: string;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string | null;
  // DS default is null for members never provisioned in GHL.
  ghl?: CustomerGhl | null;
  /**
   * Print-credit balance from the DS ledger, on `abo/customers/search` rows
   * only (abo-member-credits ADR-002): 0 when the member has no balance,
   * `null` when DS could not read balances, absent on an older DS.
   */
  credits?: number | null;
}

export interface CustomerEvent {
  _id: string;
  customer: string;
  event: string;
  metadata?: unknown;
  createdAt: string;
}

export interface CustomerSearchResponse {
  total: number;
  data: Customer[];
  page: number;
  perPage: number;
  pages: number;
}

export interface CustomerDetailsResponse {
  customer: Customer;
  lastLogin: string | null;
}

export interface CustomerActivityResponse {
  total: number;
  data: CustomerEvent[];
  page: number;
  perPage: number;
}

export interface LoginAnalyticsData {
  date: string;
  count: number;
  uniqueUsers: number;
}

export interface LoginAnalyticsSummary {
  totalLogins: number;
  uniqueUsers: number;
  avgLoginsPerDay: number;
  dateRange: {
    start: string;
    end: string;
  };
}

export interface LoginAnalyticsResponse {
  dailyLogins: LoginAnalyticsData[];
  summary: LoginAnalyticsSummary;
}

export type ActivityStatus = 'active' | 'warning' | 'inactive';

export interface CustomerWithLoginStatus extends Customer {
  daysSinceLogin: number | null;
  activityStatus: ActivityStatus;
}

export interface CustomersLoginStatusResponse {
  customers: CustomerWithLoginStatus[];
  summary: {
    active: number;
    warning: number;
    inactive: number;
    total: number;
  };
}

/** Access filter values, as stored on the DS customer (lower-case). */
export type CustomerStatusFilter = 'active' | 'inactive' | 'pending';

/**
 * Last-login filter bands (the legend's bands; the gray "no access" dot is a
 * display rule, not a band).
 */
export type LastLoginBandFilter = 'recent' | 'stale' | 'old' | 'never';

/** Columns the list can be sorted by on the server. */
export type CustomerSortBy = 'lastLoginAt' | 'createdAt';

export type SortDir = 'asc' | 'desc';

/**
 * The `accountManager` search value meaning "no manager": missing, empty, or
 * a key that is not an active manager's (DS `customer-search.query.ts`).
 */
export const ACCOUNT_MANAGER_FILTER_NONE = 'none';

export interface CustomerSearchParams {
  searchQuery?: string;
  status?: CustomerStatusFilter;
  lastLoginBand?: LastLoginBandFilter;
  /** A manager key, or `ACCOUNT_MANAGER_FILTER_NONE`. */
  accountManager?: string;
  sortBy?: CustomerSortBy;
  sortDir?: SortDir;
  /** 0-based; sent to DS as 1-based. */
  page?: number;
  perPage?: number;
}

/** Default page size of the customer search (DS uses the same default). */
export const DEFAULT_CUSTOMER_SEARCH_PER_PAGE = 15;

/**
 * The operator's IANA time zone, so DS computes the last-login band
 * boundaries on the same calendar days as the list's dot colors. `undefined`
 * when the runtime cannot tell (DS then uses UTC).
 */
export const operatorTimeZone = (): string | undefined => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone || undefined;
  } catch {
    return undefined;
  }
};

/** Query params of `GET /abo/customers/search`, without empty values. */
export const buildCustomerSearchParams = (
  params: CustomerSearchParams,
): Record<string, string | number> => {
  const query: Record<string, string | number> = {
    page: (params.page || 0) + 1, // Convert 0-based to 1-based pagination
    perPage: params.perPage || DEFAULT_CUSTOMER_SEARCH_PER_PAGE,
  };
  const searchQuery = params.searchQuery?.trim();
  if (searchQuery) query.searchQuery = searchQuery;
  if (params.status) query.status = params.status;
  if (params.lastLoginBand) query.lastLoginBand = params.lastLoginBand;
  const accountManager = params.accountManager?.trim();
  if (accountManager) query.accountManager = accountManager;
  if (params.sortBy) query.sortBy = params.sortBy;
  if (params.sortDir) query.sortDir = params.sortDir;
  const tz = operatorTimeZone();
  if (tz) query.tz = tz;
  return query;
};

/** The keys of `buildCustomerSearchParams` that are paging or sort, not filters. */
const PAGING_AND_SORT_KEYS = ['page', 'perPage', 'sortBy', 'sortDir'];

/**
 * The search filters (search text, Access, Last login, account manager, time
 * zone) of a list query, as `assign-matching` takes them: the same values the
 * list sends, without paging and sort, so DS counts exactly the listed set.
 */
export const buildCustomerSearchFilters = (
  params: CustomerSearchParams,
): Record<string, string | number> => {
  const query = buildCustomerSearchParams(params);
  PAGING_AND_SORT_KEYS.forEach((key) => delete query[key]);
  return query;
};

/** Answer of `PUT /abo/customers/:customerId/account-manager`. */
export interface CustomerAccountManagerResponse {
  customerId: string;
  accountManager: string | null;
}

export interface AssignMatchingAccountManagerRequest {
  /** From `buildCustomerSearchFilters`. */
  filters: Record<string, string | number>;
  /** An active manager's key. */
  key: string;
  /** The count the operator confirmed; DS answers 409 when its re-count differs. */
  expectedCount: number;
}

/** Answer of `POST /abo/customers/account-manager/assign-matching`. */
export interface AssignMatchingAccountManagerResponse {
  /** Members the filters matched (equal to the confirmed count). */
  matched: number;
  /** Members whose manager actually changed. */
  modified: number;
}

/** The most members one "Assign all" may change (DS refuses above it). */
export const ASSIGN_MATCHING_MAX = 5000;

export const customerAPI = {
  searchCustomers: async (params: CustomerSearchParams): Promise<CustomerSearchResponse> => {
    const response = await getInstance().get('/abo/customers/search', {
      params: buildCustomerSearchParams(params),
    });
    return response.data;
  },

  getCustomerDetails: async (customerId: string): Promise<CustomerDetailsResponse> => {
    const response = await getInstance().get(`/abo/customers/${customerId}`);
    return response.data;
  },

  getCustomerActivity: async (
    customerId: string,
    page = 0,
    perPage = 20
  ): Promise<CustomerActivityResponse> => {
    const response = await getInstance().get(`/abo/customers/${customerId}/activity`, {
      params: {
        page: page + 1, // Convert 0-based to 1-based pagination
        perPage
      },
    });
    return response.data;
  },

  getLoginAnalytics: async (params: {
    startDate: string;
    endDate: string;
    includeInactive?: boolean;
  }): Promise<LoginAnalyticsResponse> => {
    const response = await getInstance().get('/abo/customers/login-analytics', {
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
        includeInactive: params.includeInactive || false,
      },
    });
    return response.data;
  },

  getCustomerActivitySummary: async (
    accountManagerHubspotId?: string,
    searchQuery?: string
  ): Promise<{
    active: number;
    warning: number;
    inactive: number;
    total: number;
  }> => {
    const response = await getInstance().get('/abo/customers/health-summary', {
      params: {
        ...(accountManagerHubspotId && { accountManagerHubspotId }),
        ...(searchQuery && { searchQuery }),
      },
    });
    return response.data;
  },

  getCustomersWithActivityStatus: async (params: {
    page?: number;
    perPage?: number;
    activityStatus?: ActivityStatus;
    accountManagerHubspotId?: string;
    searchQuery?: string;
  }): Promise<{
    customers: CustomerWithLoginStatus[];
    total: number;
    page: number;
    perPage: number;
    pages: number;
  }> => {
    // Call the new backend endpoint that handles activity status calculation and filtering
    const response = await getInstance().get('/abo/customers/with-activity-status', {
      params: {
        page: (params.page || 0) + 1, // Convert 0-based to 1-based pagination
        perPage: params.perPage || 20,
        activityStatus: params.activityStatus,
        accountManagerHubspotId: params.accountManagerHubspotId,
        searchQuery: params.searchQuery,
      },
    });

    return {
      customers: response.data.customers,
      total: response.data.total,
      page: response.data.page - 1, // Convert back to 0-based pagination
      perPage: response.data.perPage,
      pages: response.data.pages,
    };
  },

  /**
   * Sets one member's account manager (`key`), or clears it (`null`). DS
   * verifies the operator token and checks `account_manager_admin`; the
   * operator headers go on THIS request only. Refusals: see
   * `getAccountManagerRefusal` (422 `ACCOUNT_MANAGER_NOT_ACTIVE`, 404
   * `ACCOUNT_MANAGER_MEMBER_NOT_FOUND`, 401/403/503).
   */
  setCustomerAccountManager: async (
    customerId: string,
    key: string | null,
    operator: OperatorIdentity,
  ): Promise<CustomerAccountManagerResponse> => {
    const response = await getInstance().put(
      `/abo/customers/${encodeURIComponent(customerId)}/account-manager`,
      { key },
      operatorRequestConfig(operator),
    );
    return response.data;
  },

  /**
   * Assigns one manager to every member the filters match. DS re-counts and
   * refuses with 409 `ACCOUNT_MANAGER_COUNT_CHANGED` when the count is not
   * `expectedCount`, and with 422 `ACCOUNT_MANAGER_BULK_TOO_LARGE` above
   * `ASSIGN_MATCHING_MAX`; nothing is written then.
   */
  assignMatchingAccountManager: async (
    body: AssignMatchingAccountManagerRequest,
    operator: OperatorIdentity,
  ): Promise<AssignMatchingAccountManagerResponse> => {
    const response = await getInstance().post(
      '/abo/customers/account-manager/assign-matching',
      body,
      operatorRequestConfig(operator),
    );
    return response.data;
  },
};
